/* equalTo Function from: https://github.com/jquense/yup/issues/97 */
import isValid from 'date-fns/isValid';
import { compareAsc } from 'date-fns';
import {
	addMethod as yup_addMethod,
	string as yup_string,
	date as yup_date,
} from "yup";
import { includes } from 'lodash';
import { isPossiblePhoneNumber } from "libphonenumber-js";


export const CustomYupMethods = {
	equalTo: 'equalTo',
	notEqualTo: 'notEqualTo',
	notEqualToIfNotEmpty: 'notEqualToIfNotEmpty',
	creditCard: 'creditCard',
	validDate: 'validDate',
	validShortDate: 'validShortDate',
	validPhoneNumber: 'validPhoneNumber',
	validDateRange: 'validDateRange',
	validFeedbackType: 'validFeedbackType',
	validStringInput: 'validStringInput',
	validEmail: 'validEmail',
};


const equalTo = function (ref, msg) {
	return this.test({
		name: 'equalTo',
		exclusive: false,
		message: msg || '${path} must be the same as ${reference}',
		params: {
			reference: ref.path,
		},
		test: function (value) {
			return value === this.resolve(ref);
		},
	});
};

const notEqualTo = function (ref, msg) {
	return this.test({
		name: 'equalTo',
		exclusive: false,
		message: msg || '${path} must not be the same as ${reference}',
		params: {
			reference: ref.path,
		},
		test: function (value) {
			return value !== this.resolve(ref);
		},
	});
};

const notEqualToIfNotEmpty = function (ref, msg) {
	return this.test({
		name: 'notEqualToIfNotEmpty',
		exclusive: false,
		message: msg || '${path} must not be the same as ${reference}',
		params: {
			reference: ref.path,
		},
		test: function (value) {
			return value === '' || value !== this.resolve(ref);
		},
	});
};

/**
 * Luhn algorithm in JavaScript: validate credit card number supplied as string of numbers
 * From https://gist.github.com/ShirtlessKirk/2134376
 * License forbids changing it without also renaming it
 * @author ShirtlessKirk. Copyright (c) 2012.
 * @license WTFPL (http://www.wtfpl.net/txt/copying)
 */
const luhnChkFormatted = (function (arr) {
	return function (ccNum) {
		let
			len = ccNum.length,
			bit = 1,
			sum = 0,
			val;

		while (len) {
			val = parseInt(ccNum.charAt(--len), 10);
			sum += (bit ^= 1)
				? arr[ val ]
				: val;
		}

		return sum && sum % 10 === 0;
	};
}([ 0, 2, 4, 6, 8, 1, 3, 5, 7, 9 ]));

const creditCard = function (msg) {
	return this.test({
		name: 'creditCard',
		exclusive: false,
		message: msg || 'Please enter your Bank Card number',
		params: {},
		test: function (value) {
			return luhnChkFormatted(value || '');
		},
	});
};

const checkExpireDate = function (val) {
	const pattern = /^\d{1,2}\/\d{2}$/;

	if (!pattern.test(val)) {
		return false;
	}

	const parts = val.split('/');
	// check that the month part is in a valid range
	if (parts[ 0 ] < 1 || parts[ 0 ] > 12) {
		return false;
	}

	return true;
};

const validDate = function (ref, msg) {
	return this.test({
		name: 'validDate',
		exclusive: false,
		message: 'Please enter a valid date in MM/YY format',
		params: {},
		test: function (value) {
			return checkExpireDate(value);
		},
	});
};

const checkShortDate = function (val) {
	//MM/DD/YY
	const pattern = /^\d{1,2}\/\d{2}\/\d{2}$/;

	if (!pattern.test(val)) {
		return false;
	}

	//Returns false if argument is Invalid Date and true otherwise.
	//Invalid Date is a Date, whose time value is NaN.
	if (!isValid(new Date(val))) {
		return false;
	}

	const parts = val.split('/');
	// check that the month part is in a valid range
	return !(parts[ 0 ] < 1 || parts[ 0 ] > 12);
};


const validShortDate = function (ref, msg) {
	return this.test({
		name: 'specificDate',
		exclusive: false,
		message: 'Please enter a valid date in MM/DD/YY format',
		params: {},
		test: function (value) {
			return checkShortDate(value);
		},
	});
};

const validPhoneNumber = function (countryRef, msg) {
	return this.test({
		name: 'phoneNumber',
		exclusive: false,
		message: msg || 'Please enter a valid phone number.',
		params: {
			country: countryRef.path,
		},
		test: function (value) {

			// Note: Not using `isValidPhoneNumber` here because it may one day become obsolete.
			// See https://gitlab.com/catamphetamine/libphonenumber-js#using-phone-number-validation-feature
			return isPossiblePhoneNumber(value, this.resolve(countryRef));
		},
	});
};

const validDateRange = function (ref, msg) {
	return this.test({
		name: 'validDateRange',
		exclusive: false,
		message: msg,
		params: {
			reference: ref.path,
		},
		test: function (endDateTime) {
			// CompareAsc returns -1 if the first date is before the second
			// 1 if the first date is after the second,
			// 0 if the dates are equal.
			const startDateTime = this.resolve(ref);
			return (compareAsc(startDateTime, endDateTime) <= 0);
		},
	});
};

const validFeedbackTypes = [
	'FeedbackOnWebsite',
	'GeneralFeedbackAndInquiries',
	'CardFailure',
	'PaymentFailure',
	'AccountUpdate',
	'ErrorReport',
];

const validFeedbackType = function (msg) {
	return this.test({
		name: 'feedbackType',
		exclusive: false,
		message: msg,
		params: {},
		test: function (feedbackType) {
			if (feedbackType) {
				return includes(validFeedbackTypes, feedbackType);
			}
			// an empty feedbackType handles by required field validation
			return true;
		},
	});
};

/**
 * Makes sure the value provided is a string
 */
const validStringInput = function (msg) {
	return this.test({
		name: CustomYupMethods.validStringInput,
		exclusive: false,
		message: msg,
		test: function (value) {
			if (value === undefined || value === null) {
				// a .required() test would filter these if needed
				return true;
			}

			if (typeof value !== 'string') {
				return false;
			}

			// \p{L} is any letter from any language
			// \d is any number
			return Boolean(value.match(/^[\p{L}\d ,.'-]*$/u));
		},
	});
};

const validEmail = function (msg) {
	return this.test({
		name: 'email',
		exclusive: false,
		message: msg,
		params: {},
		test: function (email) {
			if (email) {
				return Boolean(email.charAt(0).match(/^[\p{L}\d ,.'-]*$/u));
			}
			// an empty email handles by required field validation
			return true;
		},
	});
};

export const addYupMethod = (name) => {
	const methods = {
		[ CustomYupMethods.equalTo ]: () => yup_addMethod(yup_string, CustomYupMethods.equalTo, equalTo),
		[ CustomYupMethods.notEqualTo ]: () => yup_addMethod(yup_string, CustomYupMethods.notEqualTo, notEqualTo),
		[ CustomYupMethods.notEqualToIfNotEmpty ]: () => yup_addMethod(yup_string, CustomYupMethods.notEqualToIfNotEmpty, notEqualToIfNotEmpty),
		[ CustomYupMethods.creditCard ]: () => yup_addMethod(yup_string, CustomYupMethods.creditCard, creditCard),
		[ CustomYupMethods.validDate ]: () => yup_addMethod(yup_string, CustomYupMethods.validDate, validDate),
		[ CustomYupMethods.validShortDate ]: () => yup_addMethod(yup_string, CustomYupMethods.validShortDate, validShortDate),
		[ CustomYupMethods.validPhoneNumber ]: () => yup_addMethod(yup_string, CustomYupMethods.validPhoneNumber, validPhoneNumber),
		[ CustomYupMethods.validDateRange ]: () => yup_addMethod(yup_date, CustomYupMethods.validDateRange, validDateRange),
		[ CustomYupMethods.validFeedbackType ]: () => yup_addMethod(yup_string, CustomYupMethods.validFeedbackType, validFeedbackType),
		[ CustomYupMethods.validStringInput ]: () => yup_addMethod(yup_string, CustomYupMethods.validStringInput, validStringInput),
		[ CustomYupMethods.validEmail ]: () => yup_addMethod(yup_string, CustomYupMethods.validEmail, validEmail),
	};

	if (methods[ name ]) {
		methods[ name ]();
	}
	else {
		throw new Error(`Unknown yup method ${name}`);
	}
};
